import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subscription } from 'rxjs';
import {
  ConnectorCategoryDlp,
  ConnectorDetailsApiModel,
  PolicyModel,
  PolicyToUpdateModel,
} from 'src/app/models/api/governance/policy.model';
import * as _ from 'lodash';
import { ConnectorCategorySelectOptions } from 'src/app/models/selectOptions/connectorCategorySelectOptions.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { IntegrationModel } from 'src/app/models/api';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { ConfirmationDialogComponent, ConfirmData } from '../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { EffectivePolicySettingEnum } from 'src/app/services/infra/store/model/effectivePolicySetting.model';

const MAX_PAGE_SIZE = 100;
@Component({
  selector: 'app-policy-connectors',
  templateUrl: './policy-connectors.component.html',
  styleUrls: ['./policy-connectors.component.scss'],
})
export class PolicyConnectorsComponent implements OnInit, OnDestroy {
  @Input() public policyModel: PolicyModel;
  @Input() public integrationModel: IntegrationModel;
  @Input() public editable = true;
  @Output() public goBack: EventEmitter<void> = new EventEmitter<void>();
  @Output() public policyUpdated: EventEmitter<PolicyModel> = new EventEmitter<PolicyModel>();
  @Output() public notifyChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

  public PAGE_SIZE = MAX_PAGE_SIZE;

  public changesWereDone = false;

  // connector category list options
  public connectorCategorySelectOptions = ConnectorCategorySelectOptions;
  public selectedCategoryOption: ISelectOption;
  public injectCategoryOptionIndex: ReplaySubject<number> = new ReplaySubject<number>();

  // connectors for search box
  public connectorsDisplayNamesForSearch: string[] = [];
  public searchedConnectors: string[] = [];
  public resetSearchBoxBehavioralSubject$: ReplaySubject<string[] | undefined> = new ReplaySubject<
    string[] | undefined
  >();

  public EffectivePolicySettingEnum = EffectivePolicySettingEnum;

  // list of all the connectors that has been found
  public allConnectors: ConnectorDetailsApiModel[] = [];

  // list of connectors to present in page
  public connectorsToPresent: ConnectorDetailsApiModel[] = [];

  // the current connector ids that user selected
  public selectedConnectorIdsToPresent: string[];

  // mapping of category and selected connectors that will be stored in the DB
  public selectedConnectorsMapping: Map<string, string[]>;

  // paging
  public pageNumber = 1;
  public searchInProgress = false;
  public updateNumberOfConnectorsBehavioralSubject$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public resetConnectorsPages$: ReplaySubject<number> = new ReplaySubject<number>();

  // loading behavior subject
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // stores the state before changes
  private _connectorListOriginalState: { [category: string]: string[] };

  // subscriptions
  private _policyConfigUpdateSubscription$?: Subscription;
  private _policyEntitySubscription$?: Subscription;

  // confirmation dialog
  private readonly _title = 'Are you sure?';
  private readonly _content = 'You have unsaved changes that will be lost, proceed anyway?';

  constructor(
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService,
    public _matDialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.isLoading$.next(true);

    this._connectorListOriginalState = _.cloneDeep(this.policyModel.config.connectorList); // copy the original list

    this._initSelectedConnectorsMapping();

    // all connectos list - will never change
    this.allConnectors = this.policyModel.generalConnectorList;

    // get first category to present
    this.selectedCategoryOption = this.connectorCategorySelectOptions[0];
    // get selected connectors to present from configuration
    const connectorList = this.selectedConnectorsMapping.get(
      this._getConnectorCategoryEnum(this.selectedCategoryOption.value)
    );

    this.selectedConnectorIdsToPresent = connectorList ? connectorList : [];

    this.allConnectors = this._sortConnectors(this.allConnectors);

    // slice page size
    this.connectorsToPresent = this.allConnectors.slice(
      (this.pageNumber - 1) * MAX_PAGE_SIZE,
      (this.pageNumber - 1) * MAX_PAGE_SIZE + MAX_PAGE_SIZE
    );

    this._generateConnectorStringListToSearchBox();

    // it will present before searching the total number of connectors in the general list taht can be classified
    this.updateNumberOfConnectorsBehavioralSubject$.next(this.allConnectors.length);

    this.isLoading$.next(false);
  }

  public ngOnDestroy() {
    this._policyConfigUpdateSubscription$?.unsubscribe();
    this._policyEntitySubscription$?.unsubscribe();
  }

  public onCategorySelection(option: ISelectOption) {
    if (this.selectedCategoryOption.id === option.id || !this.changesWereDone) {
      this._continueToCategorySelection(option);
    } else {
      this._openConfirmationDialogDialog(option);
    }
  }

  public connectorSearched(displayNames: string[]) {
    this.searchInProgress = true;
    this.searchedConnectors = displayNames;

    // reset pages
    this.pageNumber = 1;
    this.resetConnectorsPages$.next(this.searchedConnectors.length);

    // update connectorsToPresent by the searched names
    this.connectorsToPresent = this.allConnectors.filter((connector) => displayNames.includes(connector.displayName));

    this.connectorsToPresent = this.connectorsToPresent.slice(
      (this.pageNumber - 1) * MAX_PAGE_SIZE,
      (this.pageNumber - 1) * MAX_PAGE_SIZE + MAX_PAGE_SIZE
    );

    if (this.searchedConnectors.length == this.allConnectors.length) {
      this.searchInProgress = false;
      this.searchedConnectors = [];
    }
  }

  public connectorSelected(connector: ConnectorDetailsApiModel) {
    let connectors = this.selectedConnectorsMapping.get(
      this._getConnectorCategoryEnum(this.selectedCategoryOption.value)
    );

    if (connectors) {
      const index = connectors.indexOf(connector.id);
      index > -1 ? connectors.splice(index, 1) : connectors.push(connector.id); // push or remove selected connector
    } else {
      connectors = [connector.id]; // first connector in category
    }

    this.selectedConnectorsMapping.set(this._getConnectorCategoryEnum(this.selectedCategoryOption.value), connectors);

    const connectorList = this.selectedConnectorsMapping.get(
      this._getConnectorCategoryEnum(this.selectedCategoryOption.value)
    );

    this.selectedConnectorIdsToPresent = connectorList ? connectorList : []; // update selected connector ids

    this._showSaveButtonIfNeeded();
  }

  public pageNumberGotUpdated(pageNumber: number) {
    this.pageNumber = pageNumber;

    this._updateDisplayedConnectors();
  }

  public resetForm() {
    this._initSelectedConnectorsMapping();

    // override the currecnt connector selection
    this.selectedConnectorIdsToPresent = this._connectorListOriginalState[this.selectedCategoryOption.value];

    // reset pages
    this.pageNumber = 1;
    this.resetConnectorsPages$.next(this.allConnectors.length);

    this.allConnectors = this._sortConnectors(this.allConnectors); // on the first page it will move the selection to top of the list

    // present the first connectors in the list
    this.connectorsToPresent = this.allConnectors.slice(
      (this.pageNumber - 1) * MAX_PAGE_SIZE,
      (this.pageNumber - 1) * MAX_PAGE_SIZE + MAX_PAGE_SIZE
    );

    // it will present before searching the total number of connectors in the general list taht can be classified
    this.updateNumberOfConnectorsBehavioralSubject$.next(this.allConnectors.length);

    // reset search box
    this.resetSearchBoxBehavioralSubject$.next(undefined);

    this._showSaveButtonIfNeeded();
  }

  public updatePolicyConfig() {
    this.isLoading$.next(true);
    this._generateUpdatePolicyModel();
    this._policyConfigUpdateSubscription$ = this._policiesHttpService
      .updatePolicy(this.integrationModel.id, this.policyModel.id, this._generateUpdatePolicyModel())
      .subscribe(
        () => {
          this._notificationService.success('Policy updated successfully');

          this._policyEntitySubscription$ = this._policiesHttpService
            .getPolicy(this.integrationModel.id, this.policyModel.id)
            .subscribe((policy) => {
              this.policyModel = policy;
              this._connectorListOriginalState = _.cloneDeep(this.policyModel.config.connectorList); // update original connector list

              this._showSaveButtonIfNeeded();

              this.resetForm();

              this.isLoading$.next(false);
              this.policyUpdated.emit(this.policyModel);
            });
        },
        (error) => this._httpErrorHandlerService.handle(error)
      );
  }

  public cancelSettings() {
    this.goBack.emit();
  }

  private _openConfirmationDialogDialog(option: ISelectOption) {
    const confirmtionData = {
      title: this._title,
      content: this._content,
    } as ConfirmData;
    const approveUpdatesDialogRef = this._matDialog.open(ConfirmationDialogComponent, {
      data: confirmtionData,
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    });
    approveUpdatesDialogRef.afterClosed().subscribe((userConfirmed) => {
      if (userConfirmed) {
        this.resetForm();
        this._continueToCategorySelection(option);
      } else {
        this.injectCategoryOptionIndex.next(this.connectorCategorySelectOptions.indexOf(this.selectedCategoryOption));
      }
    });
  }

  private _continueToCategorySelection(option: ISelectOption) {
    this.selectedCategoryOption = option;

    // get new connector list by selected category
    const connectorList = this.selectedConnectorsMapping.get(this._getConnectorCategoryEnum(option.value));

    this.selectedConnectorIdsToPresent = connectorList ? connectorList : [];

    this.allConnectors = this._sortConnectors(this.allConnectors);

    if (this.searchInProgress) {
      this.connectorsToPresent = this.allConnectors
        .filter((connector) => this.searchedConnectors.includes(connector.displayName))
        .slice((this.pageNumber - 1) * MAX_PAGE_SIZE, (this.pageNumber - 1) * MAX_PAGE_SIZE + MAX_PAGE_SIZE);
    } else {
      this.connectorsToPresent = this.allConnectors.slice(
        (this.pageNumber - 1) * MAX_PAGE_SIZE,
        (this.pageNumber - 1) * MAX_PAGE_SIZE + MAX_PAGE_SIZE
      );
    }
  }

  private _generateUpdatePolicyModel(): PolicyToUpdateModel {
    return {
      name: this.policyModel.name,
      description: this.policyModel.description,
      type: this.policyModel.isDefault ? undefined : this.policyModel.type,
      config: {
        allowConnectionsToProdEnvs: this.policyModel.config.allowConnectionsToProdEnvs,
        allowConnectionsToNonProdEnvs: this.policyModel.config.allowConnectionsToNonProdEnvs,
        allowedNumOfBuilders: this.policyModel.config.allowedNumOfBuilders,
        numOfHighPrivilegedUsersInEnvironment: this.policyModel.config.numOfHighPrivilegedUsersInEnvironment,
        numOfCriticalPrivilegedUsersInEnvironment: this.policyModel.config.numOfCriticalPrivilegedUsersInEnvironment,
        allowedDomains: this.policyModel.config.allowedDomains,
        connectorList: this._convertControlCategoryMapToDict(this.selectedConnectorsMapping),
        allowedIdentityProviders: this.policyModel.config.allowedIdentityProviders,
        endpoints: this.policyModel.config.endpoints,
      },
    } as PolicyToUpdateModel;
  }

  private _convertControlCategoryMapToDict(connectorMap: Map<string, string[]>): { [category: string]: string[] } {
    const connectorList: { [category: string]: string[] } = {};
    connectorMap.forEach((connectors: string[], category: string) => {
      connectorList[category] = connectors;
    });
    return connectorList;
  }

  private _initSelectedConnectorsMapping() {
    this.selectedConnectorsMapping = new Map(Object.entries(_.cloneDeep(this._connectorListOriginalState)));

    this.connectorCategorySelectOptions.forEach((option: ISelectOption) => {
      if (!this.selectedConnectorsMapping.has(option.value)) {
        this.selectedConnectorsMapping.set(option.value, []); // init category with empty connector list
      }
    });
  }

  private _getConnectorCategoryEnum(value: string): ConnectorCategoryDlp {
    return ConnectorCategoryDlp[value as keyof typeof ConnectorCategoryDlp];
  }

  private _generateConnectorStringListToSearchBox() {
    this.connectorsDisplayNamesForSearch = _.map(this.allConnectors, 'displayName');
  }

  private _sortConnectors(
    connectors: ConnectorDetailsApiModel[],
    selectedConnectors: string[] = this.selectedConnectorIdsToPresent
  ): ConnectorDetailsApiModel[] {
    const sortedConnectorList: ConnectorDetailsApiModel[] = [];

    // sory given connectors by display name
    connectors.sort((connectorA, connectorB) => (connectorA.displayName > connectorB.displayName ? 1 : -1));

    // show the selected connectors in the beginning of the list
    connectors.forEach((connector) => {
      const selectedConnector = selectedConnectors.find((connectorId) => connector.id === connectorId);
      if (selectedConnector) {
        sortedConnectorList.push(connector);
      }
    });

    connectors.forEach((connector) => {
      if (sortedConnectorList.indexOf(connector) == -1) {
        sortedConnectorList.push(connector); // push all unselected connectors
      }
    });

    return sortedConnectorList;
  }

  private _updateDisplayedConnectors() {
    if (this.searchInProgress) {
      this.connectorsToPresent = this.allConnectors
        .filter((connector) => this.searchedConnectors.includes(connector.displayName))
        .slice((this.pageNumber - 1) * MAX_PAGE_SIZE, (this.pageNumber - 1) * MAX_PAGE_SIZE + MAX_PAGE_SIZE);
    } else {
      this.connectorsToPresent = this.allConnectors.slice(
        (this.pageNumber - 1) * MAX_PAGE_SIZE,
        (this.pageNumber - 1) * MAX_PAGE_SIZE + MAX_PAGE_SIZE
      );
    }
  }

  private _showSaveButtonIfNeeded() {
    const originalConnectorList = new Map(Object.entries(this._connectorListOriginalState));

    this.changesWereDone = _.isEqual(this.selectedConnectorsMapping, originalConnectorList) ? false : true;

    this.notifyChanges.emit(this.changesWereDone);
  }
}
